import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { FounderVideo } from "@/components/FounderVideo";
import { FOUNDERS, TEAM } from "@/data/team";
import { ABOUT_VIDEO_SOURCES } from "@/config/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

const container = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };
const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const initials = (name) =>
  (name || "")
    .replace(/^CS\s+/i, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export default function AboutPage() {
  const hasFounderVideo = ABOUT_VIDEO_SOURCES.length > 0;

  useDocumentMeta({
    title: "About",
    description:
      "AMADI is a Middle East and Africa based legal consultancy advising founders, boards, and investors across the UAE and Africa.",
    path: "/about",
  });

  useJsonLd("ld-people", {
    "@context": "https://schema.org",
    "@graph": [...FOUNDERS, ...TEAM].map((f) => ({
      "@type": "Person",
      name: f.name,
      jobTitle: f.role,
      worksFor: { "@type": "LegalService", name: "AMADI Legal Consultancy" },
      ...(f.linkedin ? { sameAs: [f.linkedin] } : {}),
    })),
  });

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About"
        title="The Firm"
        subtitle="A Middle East and Africa based legal consultancy advising founders, boards, and investors across the UAE and Africa."
        current="About"
      />

      {/* The Firm */}
      <section data-testid="the-firm-section" className="bg-canvas py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
                The Firm
              </span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight">
              Who we are.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6" data-testid="the-firm-copy">
            <p className="font-serif text-base md:text-lg text-charcoal/75 leading-relaxed">
              Amadi is a next-gen advisory &amp; investment firm redefining
              business success across Africa and the Middle East.
            </p>
            <p className="font-serif text-base md:text-lg text-charcoal/75 leading-relaxed">
              Born from decades of experience across growth markets, Amadi was
              founded to be the kind of partner we once needed — one that
              understands ambition, urgency, and the nuance of execution.
            </p>
            <p className="font-serif text-base md:text-lg text-charcoal/75 leading-relaxed">
              A team of operators, founders, and investors with a shared belief
              in building things that matter.
            </p>
            <p data-testid="about-group-credit" className="font-serif text-base md:text-lg text-charcoal/60 leading-relaxed">
              AMADI Legal Consultancy FZE LLC is the legal practice of{" "}
              <a
                data-testid="about-amadi-group-link"
                href="https://amadi.io"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-forest transition-colors duration-300"
              >
                Amadi Group
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Leadership — Founders */}
      <section
        data-testid="founder-section"
        className="bg-[#F1F1EE] py-16 md:py-20 border-y border-charcoal/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-4 mb-4">
            <span className="h-px w-12 bg-gold" />
            <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
              Leadership
            </span>
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight mb-10">
            Leadership.
          </h2>

          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "0px 0px -80px 0px" }}
            className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14"
          >
            {FOUNDERS.map((f, i) => (
              <motion.div key={f.name} variants={reveal} data-testid={`founder-card-${i}`} className="group">
                <div className="w-full aspect-[4/5] bg-[#E4E4DE] border border-charcoal/15 overflow-hidden">
                  <img
                    src={f.photo}
                    alt={f.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <h3 data-testid={`founder-name-${i}`} className="font-serif text-xl md:text-2xl text-charcoal leading-tight">
                      {f.name}
                    </h3>
                    <p className="mt-2 font-sans text-[11px] tracking-[0.2em] uppercase text-gold">
                      {f.role}
                    </p>
                  </div>
                  <a
                    data-testid={`founder-linkedin-${i}`}
                    href={f.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${f.name} on LinkedIn`}
                    className="shrink-0 inline-flex items-center justify-center w-11 h-11 border border-charcoal/20 text-charcoal/70 hover:border-forest hover:text-forest transition-colors duration-300"
                  >
                    <Linkedin className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                </div>
                {f.bio && (
                  <p className="mt-4 font-serif text-sm md:text-base text-charcoal/70 leading-relaxed max-w-md">
                    {f.bio}
                  </p>
                )}
              </motion.div>
            ))}
          </motion.div>

          {/* A word from the founders — shown only when a video asset is configured */}
          {hasFounderVideo && (
            <div className="mt-16 max-w-3xl">
              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-forest mb-4">
                A Word From The Founders
              </p>
              <FounderVideo />
            </div>
          )}
        </div>
      </section>

      {/* The wider team */}
      <section data-testid="team-section" className="bg-canvas py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-4 mb-4">
            <span className="h-px w-12 bg-gold" />
            <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
              The Team
            </span>
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight mb-10">
            The wider team.
          </h2>

          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "0px 0px -80px 0px" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10"
          >
            {TEAM.map((m, i) => (
              <motion.div key={i} variants={reveal} data-testid={`team-member-${i}`} className="group">
                <div className="w-full aspect-square bg-[#E4E4DE] border border-charcoal/15 overflow-hidden flex items-center justify-center">
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} loading="lazy" decoding="async" className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]" />
                  ) : (
                    <span className="font-serif text-4xl md:text-5xl text-forest/70 select-none" aria-hidden="true">
                      {initials(m.name)}
                    </span>
                  )}
                </div>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-lg text-charcoal leading-snug">
                      {m.name || "New Team Member"}
                    </h3>
                    <p className="mt-1.5 font-sans text-[10px] tracking-[0.16em] uppercase text-gold">
                      {m.role}
                    </p>
                  </div>
                  {m.linkedin && (
                    <a
                      data-testid={`team-linkedin-${i}`}
                      href={m.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${m.name} on LinkedIn`}
                      className="shrink-0 inline-flex items-center justify-center w-9 h-9 border border-charcoal/20 text-charcoal/70 hover:border-forest hover:text-forest transition-colors duration-300"
                    >
                      <Linkedin className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </a>
                  )}
                </div>
                {m.bio && (
                  <p className="mt-3 font-serif text-sm text-charcoal/65 leading-relaxed">
                    {m.bio}
                  </p>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
